/**
 * Helpers for turning a folder selection (via an <input webkitdirectory>) or a
 * folder drag-and-drop (via the File and Directory Entries API) into a flat list
 * of files that each carry the relative folder path they came from. The upload
 * hook uses these paths to recreate the folder hierarchy on the server.
 */

export interface CollectedFile {
  file: File;
  /**
   * Relative POSIX path of the file including any parent folders that should be
   * recreated on the server, e.g. "Project/Drawings/plan.pdf". A file with no
   * folder context is just its name, e.g. "report.pdf".
   */
  path: string;
}

/** Clean a browser-supplied relative path to a POSIX path with no leading/trailing slashes. */
function normalisePath(raw: string, fallbackName: string): string {
  const cleaned = raw
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .trim();
  return cleaned.length > 0 ? cleaned : fallbackName;
}

/**
 * Build CollectedFile[] from a FileList produced by an <input> element. Handles
 * both plain multi-file inputs (empty webkitRelativePath → path is the file
 * name) and folder inputs with `webkitdirectory` (webkitRelativePath carries the
 * folder structure).
 */
export function collectInputFiles(
  fileList: FileList | File[]
): CollectedFile[] {
  return Array.from(fileList).map(file => ({
    file,
    path: normalisePath(file.webkitRelativePath ?? '', file.name),
  }));
}

/**
 * Synchronously extract FileSystemEntry objects from a drop event's DataTransfer.
 * MUST be called synchronously inside the drop handler — the DataTransferItemList
 * is cleared once the handler returns, though the returned entry objects stay
 * valid for asynchronous traversal afterwards. Returns [] when the entries API is
 * unavailable (older browsers), letting the caller fall back to dataTransfer.files.
 */
export function getDropEntries(dataTransfer: DataTransfer): FileSystemEntry[] {
  const items = dataTransfer.items;
  if (!items) {
    return [];
  }
  const entries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind !== 'file') {
      continue;
    }
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      entries.push(entry);
    }
  }
  return entries;
}

/** readEntries yields at most ~100 entries per call, so call it until it drains. */
function readAllEntries(
  reader: FileSystemDirectoryReader
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = [];
    const readBatch = () => {
      reader.readEntries(batch => {
        if (batch.length === 0) {
          resolve(all);
        } else {
          all.push(...batch);
          readBatch();
        }
      }, reject);
    };
    readBatch();
  });
}

function fileFromEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function walkEntry(
  entry: FileSystemEntry,
  prefix: string,
  out: CollectedFile[]
): Promise<void> {
  // Resilient per-entry: a file or directory the browser won't hand over is
  // skipped rather than aborting traversal of the rest of the tree.
  try {
    if (entry.isFile) {
      const file = await fileFromEntry(entry as FileSystemFileEntry);
      out.push({ file, path: prefix ? `${prefix}/${file.name}` : file.name });
      return;
    }
    if (entry.isDirectory) {
      const dirPath = prefix ? `${prefix}/${entry.name}` : entry.name;
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      const children = await readAllEntries(reader);
      await Promise.all(children.map(child => walkEntry(child, dirPath, out)));
    }
  } catch {
    // Skip this entry; keep whatever else was collected.
  }
}

/**
 * Recursively collect every file within the given dropped entries, preserving
 * each file's relative folder path. Empty directories yield no files (matching
 * the <input webkitdirectory> behaviour, which also can't see empty folders).
 */
export async function collectEntries(
  entries: FileSystemEntry[]
): Promise<CollectedFile[]> {
  const out: CollectedFile[] = [];
  await Promise.all(entries.map(entry => walkEntry(entry, '', out)));
  return out;
}

/** Split a relative path into its directory portion and leaf file name. */
export function splitPath(path: string): { dir: string; name: string } {
  const idx = path.lastIndexOf('/');
  if (idx === -1) {
    return { dir: '', name: path };
  }
  return { dir: path.slice(0, idx), name: path.slice(idx + 1) };
}

/** True when any collected file carries a folder path. */
export function hasFolders(files: CollectedFile[]): boolean {
  return files.some(f => f.path.includes('/'));
}

/**
 * All distinct directory paths implied by the files, including every ancestor,
 * sorted shallowest-first so a parent is always created before its children.
 * e.g. ["A/B/c.txt", "A/d.txt"] → ["A", "A/B"].
 */
export function distinctDirPaths(files: CollectedFile[]): string[] {
  const dirs = new Set<string>();
  for (const { path } of files) {
    const { dir } = splitPath(path);
    if (!dir) {
      continue;
    }
    let acc = '';
    for (const segment of dir.split('/')) {
      acc = acc ? `${acc}/${segment}` : segment;
      dirs.add(acc);
    }
  }
  return [...dirs].sort((a, b) => {
    const depthDiff = a.split('/').length - b.split('/').length;
    return depthDiff !== 0 ? depthDiff : a.localeCompare(b);
  });
}
